
import { supabase } from '@/integrations/supabase/client';
import { saveToLocalStorageFallback, getFromLocalStorage } from '../utils';

export interface Transfer {
  recipientNumber: string;
  amount: string;
  date: string;
}

/**
 * Get or create user ID from email for custom authentication system
 */
async function getUserIdFromEmail(email: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .eq('email', email)
      .maybeSingle();

    if (error || !data) {
      console.error('Error getting user ID from email:', error);
      return null;
    }

    return data.id;
  } catch (err) {
    console.error('Exception when getting user ID:', err);
    return null;
  }
}

/**
 * Validates transfer input data
 */
const validateTransferInput = (transfer: Transfer): string | null => {
  // Validate recipient number (basic phone number validation)
  const phoneRegex = /^\+?[\d\s\-\(\)]{8,15}$/;
  if (!transfer.recipientNumber || !phoneRegex.test(transfer.recipientNumber.replace(/\s/g, ''))) {
    return 'Please enter a valid recipient phone number';
  }

  // Validate amount (must be positive number)
  const amount = parseFloat(transfer.amount);
  if (!transfer.amount || isNaN(amount) || amount <= 0 || amount > 1000000) {
    return 'Please enter a valid amount between 0 and 1,000,000';
  }

  // Validate date
  const transferDate = new Date(transfer.date);
  if (!transfer.date || isNaN(transferDate.getTime())) {
    return 'Please enter a valid date';
  }

  // Check if date is not too far in the future (max 1 year)
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
  if (transferDate > oneYearFromNow) {
    return 'Date cannot be more than one year in the future';
  }

  return null;
};

/**
 * Function to save transfer data to Supabase with custom auth
 */
export const saveTransferToSupabase = async (transfer: Transfer, userEmail?: string): Promise<boolean> => {
  try {
    // Validate input
    const validationError = validateTransferInput(transfer);
    if (validationError) {
      console.error('Transfer validation failed:', validationError);
      return false;
    }

    if (!userEmail) {
      console.error('No user email provided for saving transfer');
      saveToLocalStorageFallback('transfers', transfer);
      return false;
    }

    const userId = await getUserIdFromEmail(userEmail);
    if (!userId) {
      console.error('No user ID found for email:', userEmail);
      saveToLocalStorageFallback('transfers', transfer);
      return false;
    }

    // Sanitize and prepare transfer data - ensure amount is a string
    const transferData = {
      recipientnumber: transfer.recipientNumber.trim(),
      amount: String(Math.round(parseFloat(transfer.amount) * 100) / 100),
      date: new Date(transfer.date).toISOString(),
      user_id: userId
    };
    
    const { error } = await supabase
      .from('transfers')
      .insert([transferData]);
    
    if (error) {
      console.error('Error saving transfer to Supabase:', error);
      saveToLocalStorageFallback('transfers', transfer);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Exception when saving transfer to Supabase:', err);
    saveToLocalStorageFallback('transfers', transfer);
    return false;
  }
};

/**
 * Function to get transfers from Supabase with custom auth
 */
export const getTransfersFromSupabase = async (date?: Date, userEmail?: string) => {
  try {
    if (!userEmail) {
      console.log('No user email provided, falling back to localStorage');
      return getFromLocalStorage<Transfer>('transfers');
    }

    const userId = await getUserIdFromEmail(userEmail);
    if (!userId) {
      console.log('No user ID found, falling back to localStorage');
      return getFromLocalStorage<Transfer>('transfers');
    }

    let query = supabase
      .from('transfers')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false });
    
    if (date) {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      
      query = query
        .gte('date', startOfDay.toISOString())
        .lte('date', endOfDay.toISOString());
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error getting transfers from Supabase:', error);
      return getFromLocalStorage<Transfer>('transfers');
    }
    
    return data || [];
  } catch (err) {
    console.error('Exception when getting transfers from Supabase:', err);
    return getFromLocalStorage<Transfer>('transfers');
  }
};
